import React, { useState } from 'react';
import { Button, Dropdown, Select, Tag, Tooltip } from 'antd';
import { usePresence } from '@src/components/Common/motion';
import {
    CheckOutlined,
    CloseOutlined,
    DownOutlined,
    EditOutlined,
    ReloadOutlined,
    SaveOutlined,
    SwapOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import { formatDateAndTime, formatDateAndTimeFromNow } from '@src/helpers/utils';
import { ViewModeSwitch } from '@src/components/UniversalExecutor/PackDataEnhancedRow';
import PipelineStageSelector from './PipelineStageSelector';
import './PipelinePanel.css';

interface PipelinePanelProps {
    /* which pipeline, and what to compare it against */
    pipelineOptions: { value: any; label: string }[] | undefined;
    displayPipelineID: any;
    displayComparePipelineID: any;
    compareMode: boolean;
    handlePipelineChange: (value: number | 'latest') => void;
    setCompareMode: (value: boolean) => void;
    setComparePipelineID: (value: 'latest' | number) => void;

    /* the stages */
    current: -1 | 0 | 1 | 2;
    onStageChange: (value: number | string) => void;
    dataPipeline: any;
    showOverview?: boolean;

    /* what can be done to it */
    toggleAppliedStatus: () => void;
    triggerAIProcessor: () => void;
    retriggerAIProcessor: () => void;

    /* how the data below is shown */
    aiProcessorResponse: any;
    /* the other side of a comparison, so both runs' details read side by side */
    compareAIProcessorResponse?: any;
    dataComparePipeline?: any;
    viewMode: 'enhanced' | 'raw';
    onViewModeChange: (mode: 'enhanced' | 'raw') => void;
    isEditing: boolean;
    onEdit: () => void;
    onSubmitEdit: () => void;
    onCancelEdit: () => void;
    switchVisible?: boolean;
    switchLabels?: { enhanced: string; raw: string };
    /** the run's own details only mean something on a stage, not on the overview */
    showDetails?: boolean;
    /** Everything that could change the Pack goes away — not greyed out, gone.
     *  What is left is the whole of the pipeline worth reading: which run,
     *  compared against which other, in which stage, and how it went. */
    readOnly: boolean;
}

/**
 * Everything you can do to a pipeline, in two lines that stay put.
 *
 * It reads down: which pipeline and what to do with it, then which stage of it,
 * on a row of its own where the stages have the width to read straight. The
 * panel sticks to the top of the scroller, so the stage you are reading is
 * always named above the data. Triggering is the button; the other two runs are
 * under its caret. Everything about the run itself — when, by which processor,
 * and the button that edits it — folds out of the stage it belongs to, which
 * unfolds by clicking the stage anywhere.
 */
const PipelinePanel = ({
    pipelineOptions,
    displayPipelineID,
    displayComparePipelineID,
    compareMode,
    handlePipelineChange,
    setCompareMode,
    setComparePipelineID,
    current,
    onStageChange,
    dataPipeline,
    showOverview,
    toggleAppliedStatus,
    triggerAIProcessor,
    retriggerAIProcessor,
    aiProcessorResponse,
    compareAIProcessorResponse,
    dataComparePipeline,
    viewMode,
    onViewModeChange,
    isEditing,
    onEdit,
    onSubmitEdit,
    onCancelEdit,
    switchVisible = true,
    switchLabels,
    showDetails = true,
    readOnly,
}: PipelinePanelProps) => {
    const [openStage, setOpenStage] = useState<0 | 1 | 2 | null>(null);

    const applied = !!dataPipeline?.applied;

    /* what a run says about itself — read the same way for either side of a
       comparison, so the two are told apart by their name and nothing else */
    const runFacts = (response: any, pipeline: any) => {
        const history = response?.state === 'OK' ? response?.ai_processor_history : null;
        const declarationVersion = pipeline?.config?.si_declaration?.version;
        const modified = response?.modified;

        if (!history && !declarationVersion && !modified)
            return <span className="pp-drawer__empty">This stage has not run yet.</span>;

        return (
            <>
                {history && (
                    <span className="pp-fact">
                        <span className="pp-fact__label">AI processor</span>
                        {history.name || 'AI Processor'}
                        {history.version && <span className="pp-chip">v{history.version}</span>}
                    </span>
                )}
                {declarationVersion && (
                    <span className="pp-fact">
                        <span className="pp-fact__label">Declaration</span>
                        <span className="pp-chip">v{declarationVersion}</span>
                    </span>
                )}
                {modified && (
                    <span className="pp-fact">
                        <span className="pp-fact__label">Last modified</span>
                        <Tooltip title={formatDateAndTime(modified)}>
                            {formatDateAndTimeFromNow(modified)}
                        </Tooltip>
                    </span>
                )}
            </>
        );
    };

    /* The drawer belongs to the stage that opened it, not to whichever stage the
       page is on this render: the two are set in separate batches, and keying it
       to `current` made it blink out and back on every switch. */
    const drawerStage = showDetails ? openStage : null;
    /* Held open while it folds away — switching stages leaves it open, so this
       only runs when the drawer itself closes. */
    const { mounted: drawerMounted, motionProps: drawerMotion } = usePresence(drawerStage !== null);

    /* Moving to a stage only moves to it: a stage arrives folded, however you
       got there, because that click was about which stage — not about its
       details. */
    const changeStage = (value: number | string) => {
        setOpenStage(null);
        onStageChange(value);
    };

    /* the details are asked for by clicking the stage you are already on */
    const toggleStage = (stage: 0 | 1 | 2) => {
        setOpenStage((was) => (was === stage ? null : stage));
    };

    /* trigger is the button — the two rarer runs sit under its caret */
    const moreActions = [
        {
            key: 'retrigger',
            icon: <ReloadOutlined />,
            label: 'Retrigger',
            onClick: retriggerAIProcessor,
        },
        {
            key: 'applied',
            icon: <CheckOutlined />,
            label: applied ? 'Mark as not applied' : 'Mark as applied',
            disabled: !dataPipeline,
            onClick: toggleAppliedStatus,
        },
    ];

    /* The drawer points at the segment it came out of: the strip is the same
       width as the drawer, so the centre of segment i is walkable in CSS from
       the strip's own padding (3px) and inter-segment gap (24px). */
    const segments = showOverview ? 4 : 3;
    const index = showOverview ? (drawerStage ?? 0) + 1 : (drawerStage ?? 0);
    const arrowLeft = `calc(3px + (100% - 6px - ${(segments - 1) * 24}px) / ${segments} * ${index + 0.5} + ${index * 24}px)`;

    return (
        <div className="pp-panel">
            <div className="pp-panel__top">
                <div className="pp-panel__pick">
                    <span className="pp-panel__label">Pipeline</span>
                    {pipelineOptions && (
                        <Select
                            size="small"
                            className="pp-panel__select"
                            value={displayPipelineID}
                            options={pipelineOptions}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore the option values are ids or "latest"
                            onChange={(value) => handlePipelineChange(value)}
                        />
                    )}
                    {applied && <Tag className="pp-panel__applied" color="green">Applied</Tag>}

                    {/* comparing is a mode, so it costs one button until it is on */}
                    <Tooltip title={compareMode ? 'Stop comparing' : 'Compare with another pipeline'}>
                        <Button
                            size="small"
                            className={`pp-panel__compare${compareMode ? ' pp-panel__compare--on' : ''}`}
                            icon={<SwapOutlined />}
                            aria-pressed={compareMode}
                            onClick={() => setCompareMode(!compareMode)}
                        >
                            Compare
                        </Button>
                    </Tooltip>
                    {compareMode && pipelineOptions && (
                        <Select
                            size="small"
                            className="pp-panel__select"
                            placeholder="Select pipeline"
                            value={displayComparePipelineID}
                            options={pipelineOptions}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore the option values are ids or "latest"
                            onChange={(value) => setComparePipelineID(value)}
                        />
                    )}
                </div>

                <div className="pp-panel__acts">
                    {isEditing && !readOnly ? (
                        <>
                            <Button size="small" type="primary" icon={<SaveOutlined />} onClick={onSubmitEdit}>Submit</Button>
                            <Button size="small" icon={<CloseOutlined />} onClick={onCancelEdit}>Cancel</Button>
                        </>
                    ) : (
                        <>
                            {switchVisible && (
                                <ViewModeSwitch viewMode={viewMode} onChange={onViewModeChange} labels={switchLabels} />
                            )}
                            {/* one split button: trigger on the face of it, the
                                rarer two under the caret — welded by CSS rather
                                than Space.Compact, which sized the two halves
                                differently once the caret went through Dropdown */}
                            {!readOnly && (
                                <div className="pp-run">
                                    <Button size="small" type="primary" icon={<ThunderboltOutlined />} onClick={triggerAIProcessor}>
                                        Trigger
                                    </Button>
                                    <Dropdown menu={{ items: moreActions }} trigger={['click']} placement="bottomRight">
                                        <Button size="small" type="primary" aria-label="More run actions">
                                            <DownOutlined />
                                        </Button>
                                    </Dropdown>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* the stages get a row of their own, and unfold underneath it */}
            <PipelineStageSelector
                current={current}
                onChange={changeStage}
                dataPipeline={dataPipeline}
                showOverview={showOverview}
                expandable={showDetails}
                expandedStage={drawerStage}
                onToggleExpand={toggleStage}
            />

            {drawerMounted && (
                <div
                    className={`pp-drawer nx-collapse${compareMode ? ' pp-drawer--sides' : ''}`}
                    style={{ '--pp-arrow': arrowLeft } as React.CSSProperties}
                    {...drawerMotion}
                >
                    <span className="pp-drawer__arrow" />
                    {compareMode ? (
                        /* comparing: a column per run, in the order the two
                           version pickers above already put them in */
                        <>
                            <div className="pp-drawer__side">
                                {runFacts(aiProcessorResponse, dataPipeline)}
                            </div>
                            <div className="pp-drawer__side">
                                {runFacts(compareAIProcessorResponse, dataComparePipeline)}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="pp-drawer__facts">{runFacts(aiProcessorResponse, dataPipeline)}</div>
                            {!isEditing && !readOnly && (
                                /* editing needs the room, so the drawer folds itself away */
                                <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => { setOpenStage(null); onEdit(); }}
                                >
                                    Edit
                                </Button>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default PipelinePanel;
